// THE LONG KINDLING — Room 23: "The Deep-wood Limit" (Act III).
// Authored from research/wall/script/v3/rooms_22-25.md §Room 23 + GAME_MAP R23 + BUILD_PLAN.md,
// copying the proven hearth_hall.ts pattern (data-only; loadRoom validates).
//
// THE FLOOR UNDER THE DREAD. There is NO mechanical puzzle here — by design. The room teaches
// ORIENTATION: three LOOK-ATs (the flattened grass, the dark, the quiet) and a leave. The unseen-worse
// is NEVER seen, NEVER named, NEVER bargained-with — held forever (the "talk to the dark" hotspot has
// Rowan reach for a word and stop himself). There is NO fire and NO watch-fire-sound here — the SILENCE
// is the horror; so NO fx fire, NO firemask. The frost won't melt; the dark ahead is unpainted.
//
// `looks_done` is a soft, monotone progress flag bumped by the three orientation look-ats (kept as a
// number so the room can advisory-track that the floor was felt). It is NOT a gate and NEVER locks
// anything — leaving is always available. The rowan-sprig (carried from R22) is the optional steadier.
//
// Painting is 384x216; the room is 480x270 (ART→ROOM ×1.25). Floor = the frost-field near the front;
// FORWARD (the unpainted dark) is deliberately OUTSIDE the walkmesh — there is no exit to it, ever.
import { loadRoom, type Room } from "../../core/schema";

export const deepWoodLimit = {
  id: "deep_wood_limit",
  name: "The Deep-wood Limit",
  size: { w: 480, h: 270 },
  background: { color: 0x070b12 },
  bgImage: "art/deep_wood_limit.png", // Retro-Diffusion painting (the limit); geometry aligned to it
  spawn: { x: 240, y: 236 },
  actorRadius: 8,
  interactRadius: 64,
  // soft orientation progress only (no puzzle, no gate, no lock). Each unique look bumps it; the room's
  // only true "move" is to leave once the floor has been felt. Never blocks anything.
  state: { looks_done: 0 },
  // walkable floor = the near frost-field band only. Forward — the unpainted dark up the slope — is
  // intentionally NOT walkable: the one held thing can never be approached.
  walkmesh: [
    [ { x: 36, y: 198 }, { x: 444, y: 198 }, { x: 460, y: 264 }, { x: 20, y: 264 } ],
  ],
  hotspots: [
    // ---- the dark ahead: never seen, never named, never resolves (SPINE lock) — look as long as you like
    { id: "the_dark", verb: "examine", label: "the dark ahead", interactAt: { x: 240, y: 210 },
      line: "There's nothing painted there. Not dark — missing. Like the hand that drew the village got to here and set the brush down and wouldn't. Rowan can look as long as he likes, and it never comes clear.",
      effect: { set: { looks_done: 1 } },
      polygon: [ { x: 168, y: 40 }, { x: 312, y: 40 }, { x: 312, y: 110 }, { x: 168, y: 110 } ] },

    // ---- the frost: day-bright and it won't melt; edges too straight to be weather
    { id: "the_frost", verb: "examine", label: "the frost", interactAt: { x: 150, y: 226 },
      line: "Day-bright and it won't melt. It stops in a line, sharp as a sill, and on the far side the grass is grey and laid flat the one way, like something heavy stood on it a long while and lifted just before I looked.",
      polygon: [ { x: 96, y: 170 }, { x: 220, y: 170 }, { x: 220, y: 196 }, { x: 96, y: 196 } ] },

    // ---- the flattened grass: the plain spatial fact the whole game rests on (one of the three looks)
    { id: "flattened_grass", verb: "examine", label: "the flattened grass", interactAt: { x: 330, y: 226 },
      line: "It comes right up to where I'm stood and stops. Whatever leans on the world out there, it leans up to here and no further. There's a reason the line's here and not at our door, and the reason's a man — old, and cold, and ours — stood back at the treeline with his back to this so none of us ever has to look at it.",
      effect: { set: { looks_done: 2 } },
      polygon: [ { x: 280, y: 170 }, { x: 404, y: 170 }, { x: 404, y: 196 }, { x: 280, y: 196 } ] },

    // ---- the trees: close as staves in a fence — the wood holding the line too, or being held
    { id: "the_trees", verb: "examine", label: "the wall of trees", interactAt: { x: 420, y: 230 },
      line: "Close as staves in a fence. You couldn't put your arm between them. This isn't where the wood gets thick — it's where the wood's holding the line too, or being held.",
      polygon: [ { x: 392, y: 20 }, { x: 444, y: 20 }, { x: 444, y: 150 }, { x: 392, y: 150 } ] },

    // ---- LISTEN (look at the quiet): no fire — the watch-fire's ABSENCE is the meter, the horror
    { id: "the_quiet", verb: "examine", label: "the quiet", interactAt: { x: 240, y: 236 },
      line: "No fire. I've never in my life been where I couldn't hear the watch-fire. The wind's not even loud — there's nothing for it to be loud against. So that's what we're keeping out. Not him. Him's the latch — him's the last warm thing between us and it, stood out here in the cold so I don't have to know its name. That's the door. And he's the only bar on it. I want to go back to the warm so bad my teeth ache.",
      effect: { set: { looks_done: 3 } },
      polygon: [ { x: 200, y: 150 }, { x: 280, y: 150 }, { x: 280, y: 170 }, { x: 200, y: 170 } ] },

    // ---- TRY TO NAME IT (talk to the dark) — Rowan reaches for a word and STOPS HIMSELF. Never named.
    { id: "name_it", verb: "talk", label: "the unspoken thing", interactAt: { x: 240, y: 224 },
      line: "There's a word for it nearly on me. The far. What's under. The cold that doesn't pass. None of them's it, and the one that is, I'll not — and he doesn't. He doesn't even get to address it. It is never named, never bargained with.",
      polygon: [ { x: 168, y: 110 }, { x: 312, y: 110 }, { x: 312, y: 150 }, { x: 168, y: 150 } ] },

    // ---- USE rowan-sprig (on the frost-sill / on self) — the optional steadier; lets him STAY and look.
    // Soft, no lock — going back to R22 for sprigs is always available; this never consumes the last one.
    { id: "use_sprig", verb: "use", label: "a rowan-sprig", interactAt: { x: 200, y: 230 },
      gatedBy: { key: "rowan_sprigs", scope: "global" },
      line: "Rowan lays a sprig on the sill of frost, or holds one in his fist, and it lets him stay in the room — steadies him enough to do the looking. Without it, on a cold return, he'd only say 'I can't stand here' and step back. Mam does this over hard letters.",
      polygon: [ { x: 174, y: 172 }, { x: 226, y: 172 }, { x: 226, y: 196 }, { x: 174, y: 196 } ] },

    // ---- TRY TO GO FORWARD — the wrong way that does no harm: his feet won't. Never lets you approach.
    { id: "go_forward", verb: "use", label: "the way forward", interactAt: { x: 240, y: 210 },
      line: "My feet won't. Good. Feet are smarter than me out here. He stops at the frost-sill the way the dog stops at the gap. No death, no penalty — the game simply will not let the one held thing be approached, ever.",
      polygon: [ { x: 200, y: 110 }, { x: 280, y: 110 }, { x: 280, y: 150 }, { x: 200, y: 150 } ] },
  ],
  // living-scene fx: NO fire, NO watch-fire (the silence IS the room). Only the cold subtraction —
  // a faint drift of frost-motes over the field; nothing warm, nothing that crackles.
  fx: [
    { id: "frost_motes", kind: "motes", layer: "front", x: 120, y: 150, w: 240, h: 70, count: 10, intensity: 0.5, seed: 1 },
  ],
  // No people. And crucially no THING — the worse is never given a shape, a face, a voice, or a deal.
  npcs: [],
  // Exits: per GAME_MAP — back to the treeline (17) and sideways to the Low (21). NO exit forward.
  // Kept commented until R17/R21 land so the world boots clean (no dangling cross-room target).
  exits: [
    // back to the treeline / the Watch (R17)
    { id: "to_treeline", target: "the_edge_dusk", spawn: { x: 300, y: 248 }, transition: "fade",
      polygon: [ { x: 36, y: 240 }, { x: 96, y: 240 }, { x: 96, y: 264 }, { x: 30, y: 264 } ] },
    // sideways to the Low / the barrow (R21)
    { id: "to_the_low", target: "the_low", spawn: { x: 250, y: 250 }, transition: "fade",
      polygon: [ { x: 392, y: 240 }, { x: 452, y: 240 }, { x: 452, y: 264 }, { x: 392, y: 264 } ] },
  ],
};

export const room: Room = loadRoom(deepWoodLimit);
